<template>
    <div class="flex items-center justify-center gap-2 relative" ref="containerRef">
        <div ref="buttonRef" class="flex flex-row items-center justify-center border border-gray-200 dark:border-gray-600 rounded-full p-2 md:p-3 bg-[#ffffff] dark:bg-gray-800 shadow-md transition-colors duration-150 z-50"
            :class="{'cursor-pointer hover:bg-gradient-to-r hover:text-white hover:border-transparent hover:bg-gradient hover:from-indigo-500 hover:to-purple-500 dark:hover:bg-gray-700': !open,
                'cursor-pointer hover:text-slate-700 hover:bg-gray-200': open,
            }"
            @click="toggleMenu()">
            <div class="relative md:size-6 size-5 justify-center items-center" >
                <Transition name="slide-up">
                    <Tooltip text="Options" position="left" v-if="!open" >
                        <div class="cursor-pointer md:size-6 size-5 absolute top-0 left-0">
                            <SquaresPlusIcon class="md:w-6 md:h-6 w-5 h-5" />
                        </div>
                    </Tooltip>
                </Transition>
                <Transition name="slide-up">
                    <Tooltip text="Close options" position="left" v-if="open" >
                        <div class="cursor-pointer md:size-6 size-5 absolute top-0 left-0">
                            <XMarkIcon class="md:w-6 md:h-6 w-5 h-5" />
                        </div>
                    </Tooltip>
                </Transition>
            </div>

        </div>
        <Transition :name="props.direction === 'horizontal' ? 'slide-left' : 'slide-left'" mode="out-in">
            <div ref="menuRef" class="absolute w-fit h-fit items-center justify-center gap-2 border-transparent z-[49]" v-if="open"
                :class="mapMenuPosition[props.menuPosition]">
                <MenuList :menus="menus" :footer="props.footer" 
                    @menu-click="onMenuClick"
                    @favorite-change="onFavoriteChange"
                    @loaded="onLoaded" />
            </div>
        </Transition>
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useMagicKeys, whenever } from '@vueuse/core'
import Tooltip from '@/components/common/elements/TriadTooltip.vue'
import MenuList from '@/components/common/menus/MenuList.vue'
import { SquaresPlusIcon, XMarkIcon } from '@heroicons/vue/24/outline'
const emit = defineEmits(['toggleMenu', 'favorite-change', 'loaded']);
const props = defineProps({
    open: {
        type: Boolean,
        default: false
    },
    footer: {
        type: Object,
        default: null,
        validator: (footer) => {
            if (!footer) return true
            return footer.date && footer.lastEditedBy
        }
    },
    direction: {
        type: String,
        default: 'horizontal',
        validator: (value) => {
            return ['horizontal', 'vertical'].includes(value);
        }
    },
    menuPosition: {
        type: String,
        default: 'bottom-right',
        validator: (value) => {
            return ['bottom-right', 'bottom-left', 'top-right', 'top-left'].includes(value);
        }
    },
    menus: {
        type: Array,
        required: true
    }
});

const open = ref(props.open);
const containerRef = ref(null);
const buttonRef = ref(null);
const menuRef = ref(null);

const mapMenuPosition = {
    'bottom-left': 'bottom-14 left-4 md:bottom-full md:left-full',
    'bottom-right': 'bottom-14 right-4 md:bottom-full md:right-full',
    'top-right': 'top-14 right-4 md:top-full md:right-full',
    'top-left': 'top-14 left-4 md:top-full md:left-full',
}


const toggleMenu = () => {
    open.value = !open.value;
    emit('toggleMenu', open.value);
}

const closeMenu = () => {
    if (open.value) {
        open.value = false;
        emit('toggleMenu', false);
    }
}

const handleClickOutside = (event) => {
    if (!open.value) return;
    
    // Check if click is outside the container (button + menu)
    if (containerRef.value && !containerRef.value.contains(event.target)) {
        closeMenu();
    }
}

const onFavoriteChange = (menu) => {
    emit('favorite-change', menu);
}
// Fecha o popup após ação de um item, exceto os que mantêm painel inline
// aberto para interação contínua (transpose, autoscroll, zoom -> keepOpen: true).
// Itens com toggle switch nem emitem menu-click, então já permanecem abertos.
const onMenuClick = (menu) => {
    if (menu && menu.keepOpen) return;
    closeMenu();
}
const onLoaded = (data) => {
    emit('loaded', data);
}

const { current } = useMagicKeys();

whenever(current, (value) => {
    const shortcutEvent = {shortcut: ""};
    const currentKeys = [];
    value.forEach(key => {
        currentKeys.push(key.toLowerCase());
    })
    shortcutEvent.shortcut = currentKeys.join('_');
    
    props.menus.forEach(menu => {
        if (menu.onShortcut && menu.shortcutKeys) {
            if (menu.shortcutKeys[0] instanceof Array) {
                menu.shortcutKeys.forEach(key => {
                    if (shortcutEvent.shortcut === key.map(key => key.toLowerCase()).join('_')) {
                        menu.onShortcut(shortcutEvent.shortcut);
                    }
                });
            }else {
                if (shortcutEvent.shortcut === menu.shortcutKeys.map(key => key.toLowerCase()).join('_')) {
                    menu.onShortcut(menu.toggleValue);
                }
            }
        }
    });
});

// Watch for open state changes to add/remove click listener
watch(open, (isOpen) => {
    if (isOpen) {
        // Use nextTick to ensure DOM is updated before adding listener
        nextTick(() => {
            document.addEventListener('click', handleClickOutside);
        });
    } else {
        document.removeEventListener('click', handleClickOutside);
    }
});

onMounted(() => {
    // If menu is already open on mount, add listener
    if (open.value) {
        nextTick(() => {
            document.addEventListener('click', handleClickOutside);
        });
    }
});

onUnmounted(() => {
    // Clean up event listener
    document.removeEventListener('click', handleClickOutside);
});

</script>